/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useCallback, createContext, useContext } from 'react'

import {
  Account,
  syncLinkManuallyByUniqueIdentifier,
} from '../../../../mogul/core/_requests'
import { APIError } from '../../../../app/modules/auth'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import useAccounts from '../../../../mogul/hooks/useAccounts'
import MogulHeader from '../../../../mogul/components/MogulHeader'
import { PassareAcquaintancesTable } from './lead-tables/PassareAcquaintancesTable'
import { FullCircleLeadsTable } from './lead-tables/FullCircleLeadsTable'
import { AbandonedCartsTable } from './lead-tables/PortalLeadsTable'
import { DirectMailRepliesTable } from './lead-tables/DirectMailRepliesTable'
import { WebhooksTable } from './lead-tables/WebhooksTable'
import { EventCheckInsTable } from './lead-tables/EventCheckInsTable'
import { EventAdminsTable } from './lead-tables/EventAdmins'

type LeadDetailContextProps = {
  accounts: Account[],
}

const LeadDetailContext = createContext<LeadDetailContextProps>({
  accounts: [],
})

export const useLeadDetail = () => {
  return useContext(LeadDetailContext)
}

export function SyncLeadButton({
  uniqueIdentifier, disabled = false, isSecondary = false, onSynced
}: {
  uniqueIdentifier: string, disabled?: boolean, isSecondary?: boolean,
  // When provided, called with the sync response instead of reloading the page —
  // lets the parent table update the lead's row in place.
  onSynced?: (result: any) => void
}) {

  const [syncing, setSyncing] = useState(false)
  const [syncError, setSyncError] = useState<string | null>(null)

  const onSyncClicked = useCallback(async (syncUniqueIdentifier: string) => {
    setSyncing(true)
    setSyncError(null)
    // todo
    try {
      const result = await syncLinkManuallyByUniqueIdentifier(syncUniqueIdentifier)
      // A 200 can still be a no-op (e.g. a per-branch routing skip). Surface it
      // instead of silently reloading, which previously made the button look
      // like it did nothing.
      if (result && result.skipped) {
        setSyncError(`Not synced — lead was skipped (${result.skip_reason || 'suppressed'}).`)
        setSyncing(false)
        return
      }
      // Synced — let the parent update the row in place when it knows how;
      // otherwise reload to reflect the new state (tables not yet wired).
      if (onSynced) {
        setSyncing(false)
        onSynced(result)
        return
      }
      window.location.reload()
    } catch (exception) {
      console.error(exception)
      const errorMessage =
        exception instanceof APIError ? exception.message : 'Failed to sync'
      console.error(errorMessage)
      setSyncError(errorMessage)
      setSyncing(false)
    }
  }, [onSynced])

  return <div>
    <div>
      {syncError && <div className='alert alert-danger' role='alert'>
        {syncError}
      </div>}
    </div>
    <button
      className={!isSecondary ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
      disabled={disabled || syncing}
      onClick={() => onSyncClicked(uniqueIdentifier)}>
      {!isSecondary ? `Sync to 360` : `Re-sync to 360`}
    </button>
  </div>
}

function LeadList({ children }: { children: React.ReactNode }) {

  const {
    accounts,
    loadingAccounts,
  } = useAccounts()

  if (loadingAccounts) {
    return <div className='mt-5'>
      <div className="spinner-border" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  }

  return <LeadDetailContext.Provider value={{
    accounts
  }}>
    {children}
  </LeadDetailContext.Provider>
}

export function DebugAllLeads() {

  const { pathname } = useLocation()

  return (
    <>
      {/* Manage Premier Preneed */}
      <MogulHeader title={`All Leads`} />
      <div className='row gy-5 g-xl-8' style={{ height: '100%' }}>
        <div className='col-xxl-12'>
          <div className="header-menu align-items-stretch mb-5">
            <div className="menu menu-lg-rounded menu-column menu-lg-row menu-state-bg menu-title-gray-700 menu-state-title-primary menu-state-icon-primary menu-state-bullet-primary menu-arrow-gray-400 fw-bold my-5 my-lg-0 align-items-stretch">
              <div className="menu-item me-lg-1">
                <Link className={["menu-link py-3 btn btn-link-primary", pathname === `/debugging/all-leads/event-admins` ? "active" : ""].join(" ")}
                  to={`/debugging/all-leads/event-admins`}
                >
                  <span className="menu-title">
                    Admins
                  </span>
                </Link>
              </div>
              <div className="menu-item me-lg-1">
                <Link
                  className={["menu-link py-3 btn btn-link-primary", pathname === `/debugging/all-leads/event-check-ins` ? "active" : ""].join(" ")}
                  to={`/debugging/all-leads/event-check-ins`}
                >
                  <span className="menu-title">
                    Check-Ins
                  </span>
                </Link>
              </div>
              <div className="menu-item me-lg-1">
                <Link
                  className={["menu-link py-3 btn btn-link-primary", pathname === `/debugging/all-leads/direct-mail-replies` ? "active" : ""].join(" ")}
                  to={`/debugging/all-leads/direct-mail-replies`}
                >
                  <span className="menu-title">
                    Direct Mail
                  </span>
                </Link>
              </div>
              <div className="menu-item me-lg-1">
                <Link
                  className={["menu-link py-3 btn btn-link-primary", pathname.startsWith(`/debugging/all-leads/passare-acquaintances`) ? "active" : ""].join(" ")}
                  to={`/debugging/all-leads/passare-acquaintances`}
                >
                  <span className="menu-title">
                    Passare
                  </span>
                </Link>
              </div>
              <div className="menu-item me-lg-1">
                <Link
                  className={["menu-link py-3 btn btn-link-primary", pathname === `/debugging/all-leads/full-circle-leads` ? "active" : ""].join(" ")}
                  to={`/debugging/all-leads/full-circle-leads`}
                >
                  <span className="menu-title">
                    FCA
                  </span>
                </Link>
              </div>
              <div className="menu-item me-lg-1">
                <Link
                  className={["menu-link py-3 btn btn-link-primary", pathname === `/debugging/all-leads/abandoned-carts` ? "active" : ""].join(" ")}
                  to={`/debugging/all-leads/abandoned-carts`}
                >
                  <span className="menu-title">
                    Portals
                  </span>
                </Link>
              </div>
              <div className="menu-item me-lg-1">
                <Link
                  className={["menu-link py-3 btn btn-link-primary", pathname.startsWith(`/debugging/all-leads/webhooks`) ? "active" : ""].join(" ")}
                  to={`/debugging/all-leads/webhooks`}
                >
                  <span className="menu-title">
                    Webhooks
                  </span>
                </Link>
              </div>
            </div>
          </div>
          {/* card-xxl-stretch forces the card to full column height. The other
              tabs scroll with the page and want that. The Passare table sizes
              its own scroll pane to the viewport, so stretching only adds ~100px
              of empty card under the last row — let that one size to content. */}
          <div className={`card ${pathname.startsWith('/debugging/all-leads/passare-acquaintances') ? '' : 'card-xxl-stretch'} mb-5 mb-xl-8`}>
            <Routes>
              <Route path='event-admins' element={<LeadList>
                <EventAdminsTable />
              </LeadList>} />
              <Route path='event-check-ins' element={<LeadList>
                <EventCheckInsTable />
              </LeadList>} />
              <Route path='direct-mail-replies' element={<LeadList>
                <DirectMailRepliesTable />
              </LeadList>} />
              <Route path='passare-acquaintances/*' element={<LeadList>
                <PassareAcquaintancesTable />
              </LeadList>} />
              <Route path='full-circle-leads' element={<LeadList>
                <FullCircleLeadsTable />
              </LeadList>} />
              <Route path='abandoned-carts' element={<LeadList>
                <AbandonedCartsTable />
              </LeadList>} />
              <Route path='webhooks/*' element={<LeadList>
                <WebhooksTable />
              </LeadList>} />
              <Route path='*' element={<Navigate to='/debugging/all-leads/event-admins' />} />
            </Routes>
          </div>
        </div>
      </div>
    </>
  )
}